/**
 * Contract scalars ⇄ domain values, matching ios/KashFlowTests/Contract/ContractCodec.swift:
 * decimals are Decimal.description strings, doubles are Double.description strings (with
 * "NaN"/"Infinity"), dates are "YYYY-MM-DD", and a JSON null reads the same as an absent key.
 */
import { formatDay, parseDay, type Day } from '../core/calendar.ts';
import { formatDecimal, parseDecimal, type Decimal } from '../core/decimal.ts';
import type { CalcContext } from '../types/domain.ts';

export { encodeDouble } from '../core/double.ts';

/** The clock the Swift runner pins when a case has no `asOf`. */
export const DEFAULT_AS_OF = '1970-01-01';

export const decodeDecimal = (value: string): Decimal => parseDecimal(value);
export const decodeDay = (value: string): Day => parseDay(value);

/** Map a possibly absent/null wire value; absent and null both become null. */
export const optional = <T, R>(value: T | null | undefined, decode: (value: T) => R): R | null =>
  value === undefined || value === null ? null : decode(value);

export const optionalDecimal = (value: string | null | undefined): Decimal | null => optional(value, decodeDecimal);
export const optionalDay = (value: string | null | undefined): Day | null => optional(value, decodeDay);
export const orDefault = <T>(value: T | null | undefined, fallback: T): T =>
  value === undefined || value === null ? fallback : value;

export const encodeDecimal = (value: Decimal): string => formatDecimal(value);
export const encodeOptionalDecimal = (value: Decimal | null): string | null => (value === null ? null : formatDecimal(value));
export const encodeDay = (value: Day): string => formatDay(value);
export const encodeOptionalDay = (value: Day | null): string | null => (value === null ? null : formatDay(value));

export const contextFrom = (input: { readonly asOf?: string | null }): CalcContext => ({
  now: decodeDay(input.asOf ?? DEFAULT_AS_OF),
});
